"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
exports.__esModule = true;
exports.default = exports.CalendarContext = exports.CalendarConsumer = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _objectWithoutPropertiesLoose2 = _interopRequireDefault(require("@babel/runtime/helpers/objectWithoutPropertiesLoose"));
var _react = _interopRequireWildcard(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _reactBigCalendar = require("react-big-calendar");
exports.dateFnsLocalizer = _reactBigCalendar.dateFnsLocalizer;
exports.momentLocalizer = _reactBigCalendar.momentLocalizer;
exports.globalizeLocalizer = _reactBigCalendar.globalizeLocalizer;
exports.views = _reactBigCalendar.Views;
var _styles = _interopRequireDefault(require("../styles"));
var _withWidth = _interopRequireDefault(require("../withWidth"));
var _EDSContext = require("../EDSContext/EDSContext");
var _classnames = _interopRequireDefault(require("classnames"));
var _CalendarToolbar = _interopRequireDefault(require("./CalendarToolbar"));
var _EventWrapper = _interopRequireDefault(require("./EventWrapper"));
var _ReactBigCalendarStyles = _interopRequireDefault(require("./internal/ReactBigCalendarStyles"));
var _CalendarEvent = _interopRequireDefault(require("./internal/CalendarEvent"));
var _dateFns = require("date-fns");
var _Locales = _interopRequireDefault(require("../StandaloneDatePicker/internal/Locales"));
var _cloneDeep = _interopRequireDefault(require("lodash/cloneDeep"));
var _excluded = ["classes", "compact", "date", "defaultDate", "defaultView", "ToolbarDropdownProps", "edsContext", "formats", "localizer", "legend", "onNavigate", "showViewNames", "showOnlyDay", "width", "onSelectEvent", "onKeyPressEvent", "popperContent", "showMultiDayTimes", "titleAccessor", "components"]; // Our custom components
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function _getRequireWildcardCache(e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && {}.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
var CalendarContext = exports.CalendarContext = /*#__PURE__*/(0, _react.createContext)();
var Provider = CalendarContext.Provider,
  CalendarConsumer = exports.CalendarConsumer = CalendarContext.Consumer;
var dateFormat = _propTypes.default.any;
var dateRangeFormat = _propTypes.default.func;
var CalendarProvider = function CalendarProvider(props) {
  var compact = props.compact,
    defaultDate = props.defaultDate,
    ToolbarDropdownProps = props.ToolbarDropdownProps,
    showViewNames = props.showViewNames,
    view = props.view,
    width = props.width;
  var _useState = (0, _react.useState)(defaultDate),
    date = _useState[0],
    setDate = _useState[1];
  var _useState2 = (0, _react.useState)(view),
    selectedView = _useState2[0],
    setSelectedView = _useState2[1];
  return /*#__PURE__*/_react.default.createElement(Provider, {
    value: {
      compact: compact,
      date: date,
      ToolbarDropdownProps: ToolbarDropdownProps,
      setDate: setDate,
      selectedView: selectedView,
      setSelectedView: setSelectedView,
      showViewNames: showViewNames,
      width: width
    }
  }, props.children);
};
CalendarProvider.propTypes = {
  compact: _propTypes.default.bool,
  children: _propTypes.default.object.isRequired,
  defaultDate: _propTypes.default.instanceOf(Date),
  ToolbarDropdownProps: _propTypes.default.object,
  showViewNames: _propTypes.default.bool,
  view: _propTypes.default.string.isRequired,
  width: _propTypes.default.string.isRequired
};

/**
 * A component for displaying events within a week or day view.
 * @done true
 * @updated v7.16.0
 * @versionAdded v0.2.1
 * @examples
 *  CalendarSingleDay
 *  CalendarExample
 *  CalendarI18n
 *  ClickEvents
 *  CalendarWithPopperAsNode
 *  CalendarWithPopperAsRenderFunction
 *  CustomComponentExample
 */
var Calendar = /*#__PURE__*/_react.default.forwardRef(function (props, ref) {
  var containerRef = _react.default.useRef(null);
  var _useState3 = (0, _react.useState)({
      open: false,
      target: undefined
    }),
    popper = _useState3[0],
    setPopper = _useState3[1];
  var classes = props.classes,
    compact = props.compact,
    dateProp = props.date,
    defaultDate = props.defaultDate,
    defaultView = props.defaultView,
    ToolbarDropdownProps = props.ToolbarDropdownProps,
    edsContext = props.edsContext,
    formats = props.formats,
    localizerProp = props.localizer,
    legend = props.legend,
    onNavigate = props.onNavigate,
    showViewNames = props.showViewNames,
    showOnlyDay = props.showOnlyDay,
    width = props.width,
    onSelectEvent = props.onSelectEvent,
    onKeyPressEvent = props.onKeyPressEvent,
    popperContent = props.popperContent,
    showMultiDayTimes = props.showMultiDayTimes,
    titleAccessor = props.titleAccessor,
    componentsProp = props.components,
    rest = (0, _objectWithoutPropertiesLoose2.default)(props, _excluded);

  /**
   * Utility function to remove some of the a11y-roles that react-big-calendar applies by default.
   * These roles cause a11y issues from page-scans and need to be removed to pass a11y tests.
   * We also add a tab-index to the scrollable time-content container from rbc, to allow keyboard nav
   * users to easily scroll the time-content element.
   */
  var accessibilizeHtml = function accessibilizeHtml() {
    var _containerRef$current, _containerRef$current2, _containerRef$current3, _containerRef$current4;
    var allDayCell = containerRef == null || (_containerRef$current = containerRef.current) == null ? void 0 : _containerRef$current.querySelector('.rbc-allday-cell');
    allDayCell.removeAttribute('role');
    var rowContent = containerRef == null || (_containerRef$current2 = containerRef.current) == null ? void 0 : _containerRef$current2.querySelector('.rbc-row-content');
    rowContent.removeAttribute('role');
    var timeContent = containerRef == null || (_containerRef$current3 = containerRef.current) == null ? void 0 : _containerRef$current3.querySelector('.rbc-time-content');
    timeContent.setAttribute('tabindex', '0');
    var timeColumn = containerRef == null || (_containerRef$current4 = containerRef.current) == null ? void 0 : _containerRef$current4.getElementsByClassName('rbc-time-gutter rbc-time-column');
    // there should only be one
    // apply aria-hidden to skip screen reader from reading every single time-label in the column
    timeColumn[0].setAttribute('aria-hidden', 'true');
  };
  _react.default.useEffect(function () {
    // Options for the observer (which mutations to observe)
    var config = {
      childList: true,
      subtree: true
    };
    var observer = new MutationObserver(function () {
      // always apply a11y overrides
      accessibilizeHtml();
    });
    if (containerRef != null && containerRef.current) {
      observer.observe(containerRef.current, config);
    }
    accessibilizeHtml();
    return function () {
      return observer.disconnect();
    }; // Cleanup on unmount
  }, []);

  // Custom messages to send to component
  var overrideMessages = {
    agenda: edsContext.formatMessage('component.Calendar.agenda'),
    allDay: edsContext.formatMessage('component.Calendar.allDay'),
    date: edsContext.formatMessage('component.Calendar.date'),
    day: edsContext.formatMessage('component.Calendar.day'),
    event: edsContext.formatMessage('component.Calendar.event'),
    month: edsContext.formatMessage('component.Calendar.month'),
    next: edsContext.formatMessage('component.Calendar.next'),
    noEventsInRange: edsContext.formatMessage('component.Calendar.noEventsInRange'),
    previous: edsContext.formatMessage('component.Calendar.previous'),
    time: edsContext.formatMessage('component.Calendar.time'),
    today: edsContext.formatMessage('component.Calendar.goToToday'),
    week: edsContext.formatMessage('component.Calendar.week'),
    work_week: edsContext.formatMessage('component.Calendar.workWeek')
  };

  // EDSApplication-level locale
  // Formatting ensures the locale string matches locales object in the localizer
  var locale = edsContext.locale;

  // Custom date formats are passed directly to the localizer object to prevent formatting conflicts
  // with other localizers. Date-fns format tokens differ from Moment.js and other libraries
  var calendarLocalizer = (0, _react.useMemo)(function () {
    if (localizerProp) {
      return localizerProp;
    }
    var defaultLocalizer = (0, _reactBigCalendar.dateFnsLocalizer)({
      format: _dateFns.format,
      parse: _dateFns.parse,
      startOfWeek: _dateFns.startOfWeek,
      getDay: _dateFns.getDay,
      locales: _Locales.default
    });
    var defaultLocalizerClone = (0, _cloneDeep.default)(defaultLocalizer);
    var localeUses24HourTime = function localeUses24HourTime() {
      return (0, _dateFns.format)(new Date(2020, 0, 1, 13), 'p', {
        locale: _Locales.default[locale]
      }).split(':')[0].length === 2;
    };
    defaultLocalizerClone.formats.dayFormat = function (date, calendarCulture, localizer) {
      switch (width) {
        case 'sm':
          return localizer.format(date, 'EEE d', calendarCulture);
        case 'xs':
          return localizer.format(date, 'EEE', calendarCulture);
        default:
          return localizer.format(date, 'EEEE d', calendarCulture);
      }
    };
    defaultLocalizerClone.formats.dayHeaderFormat = function (date, calendarCulture, localizer) {
      return localizer.format(date, showOnlyDay ? 'EEEE' : 'EEEE, MMM d, u', calendarCulture);
    };
    defaultLocalizerClone.formats.dayRangeHeaderFormat = function (_ref, calendarCulture, localizer) {
      var start = _ref.start,
        end = _ref.end;
      var startYear = start.getFullYear();
      var endYear = end.getFullYear();
      var startFormat = localizer.format(start, startYear === endYear ? 'MMM d' : 'MMM d, u', calendarCulture);
      var endFormat = localizer.format(end, 'MMM d, u', calendarCulture);
      return startFormat + " \u2013 " + endFormat;
    };
    if (compact) {
      defaultLocalizerClone.formats.timeGutterFormat = function (date, localCulture, localizer) {
        if (localeUses24HourTime(locale)) {
          return localizer.format(date, 'H', localCulture);
        } else {
          return localizer.format(date, 'h aa', localCulture);
        }
      };
    }
    return defaultLocalizerClone;
  }, [localizerProp, width, showOnlyDay, locale, compact]);
  var handleNavigation = function handleNavigation(callback) {
    return function (date) {
      setPopper({
        open: false
      });
      callback(date);
    };
  };
  var compactGutterHeader = function compactGutterHeader() {
    return /*#__PURE__*/_react.default.createElement("div", {
      className: 'rbc-gutterHeader'
    }, edsContext.formatMessage('component.Calendar.allDay'));
  };
  var localPopperOpen = function localPopperOpen(localOpen) {
    setPopper({
      open: localOpen
    });
  };
  var onSelectEventClick = function onSelectEventClick(eventData, e) {
    e.preventDefault();
    popperContent && setPopper({
      open: !popper.open,
      target: e.currentTarget
    });
    onSelectEvent && onSelectEvent(eventData, e);
  };
  var handleOnKeyPressEvent = function handleOnKeyPressEvent(eventData, e) {
    // Open Popper only when Enter is pressed
    if (e.key === 'Enter') {
      popperContent && setPopper({
        open: true,
        target: e.currentTarget
      });
      onKeyPressEvent && onKeyPressEvent(eventData, e);
    } else {
      // else do keypress event
      onKeyPressEvent && onKeyPressEvent(eventData, e);
    }
  };
  var handleAllDayEventTitle = function handleAllDayEventTitle(event) {
    if (titleAccessor !== 'title' && typeof titleAccessor === 'function') {
      return titleAccessor(event);
    } else {
      var midnight = new Date(2022, 0, 1, 0, 0, 0);
      if ((!showMultiDayTimes || event.allDay) && (event.start.toLocaleTimeString('en-US', 'full') !== midnight.toLocaleTimeString('en-US', 'full') || event.end.toLocaleTimeString('en-US', 'full') !== midnight.toLocaleTimeString('en-US', 'full')) && event.start.toDateString() !== event.end.toDateString()) {
        return (0, _dateFns.format)(event.start, 'h:mm a', {
          locale: _Locales.default[locale]
        }) + " \u2013 " + event.title;
      } else {
        return event.title;
      }
    }
  };
  var defaultComponentsProp = {
    // eslint-disable-next-line react/display-name
    event: function event(_event) {
      return /*#__PURE__*/_react.default.createElement(_CalendarEvent.default, {
        popperContent: popperContent,
        open: popper.open,
        target: popper.target,
        event: _event,
        popperOpen: localPopperOpen
      });
    },
    toolbar: _CalendarToolbar.default,
    eventWrapper: _EventWrapper.default,
    timeGutterHeader: compact ? compactGutterHeader : undefined,
    // both week and work_week need to have their headers overridden to resolve a11y issues
    // rbc's original Header code applies a role and aria-sort attributes which violate the default HTML given
    // https://github.com/jquense/react-big-calendar/blob/v0.40.8/src/Header.js
    week: {
      header: function header(_ref2) {
        var label = _ref2.label;
        return /*#__PURE__*/_react.default.createElement("span", null, label);
      }
    },
    work_week: {
      header: function header(_ref3) {
        var label = _ref3.label;
        return /*#__PURE__*/_react.default.createElement("span", null, label);
      }
    }
  };
  var components = Object.assign(defaultComponentsProp, componentsProp);
  return /*#__PURE__*/_react.default.createElement(CalendarProvider, {
    compact: compact,
    date: defaultDate,
    ToolbarDropdownProps: ToolbarDropdownProps,
    showViewNames: showViewNames,
    view: defaultView,
    width: width
  }, /*#__PURE__*/_react.default.createElement(CalendarConsumer, null, function (context) {
    return /*#__PURE__*/_react.default.createElement("div", {
      className: (0, _classnames.default)('rbc-container', {
        'rbc-highlight-today': context.selectedView !== 'day',
        'rbc-compact': compact
      }),
      ref: containerRef
    }, /*#__PURE__*/_react.default.createElement(_reactBigCalendar.Calendar, (0, _extends2.default)({
      className: classes.root,
      defaultDate: defaultDate,
      defaultView: defaultView,
      date: dateProp || context.date,
      messages: overrideMessages,
      onNavigate: onNavigate || handleNavigation(context.setDate),
      localizer: calendarLocalizer,
      formats: formats,
      culture: locale,
      rtl: edsContext.dir === 'rtl',
      components: components,
      onSelectEvent: onSelectEventClick,
      onKeyPressEvent: handleOnKeyPressEvent,
      showMultiDayTimes: showMultiDayTimes,
      titleAccessor: handleAllDayEventTitle
    }, rest, {
      ref: ref
    })), legend);
  }));
});
Calendar.propTypes = {
  /**
   * @ignore
   *
   * Used to show events in different views when there are overlapping times.
   */
  dayLayoutAlgorithm: _propTypes.default.oneOfType([_propTypes.default.oneOf(['overlap', 'no-overlap']), _propTypes.default.func]),
  /**
   * Classes used to extend original style object.
   */
  classes: _propTypes.default.object,
  /**
   * @ignore
   * **BETA** Applies compact variant styles to calendar.
   */
  compact: _propTypes.default.bool,
  /**
   * Object to override the date/time library handler.
   */
  localizer: _propTypes.default.object,
  /**
   * Props passed to main calendar `<div>`.
   */
  elementProps: _propTypes.default.object,
  /**
   * The current date value of the calendar. Determines the visible view range.
   * If `date` is omitted then the result of `getNow` is used; otherwise the
   * current date is used.
   *
   * `@controllable onNavigate`
   */
  date: _propTypes.default.instanceOf(Date),
  /**
   * The initial date set for the Calendar.
   */
  defaultDate: _propTypes.default.instanceOf(Date),
  /**
   * The current view of the calendar.
   *
   * `@default 'month'`
   * `@controllable onView`
   */
  view: _propTypes.default.string,
  /**
   * The initial view set for the Calendar.
   *
   */
  defaultView: _propTypes.default.oneOf(['month', 'week', 'work_week', 'day', 'agenda']),
  /**
    * Attributes applied to the `Dropdown` element in the 'CalendarToolbar'.
   */
  ToolbarDropdownProps: _propTypes.default.object,
  /**
   * An array of event objects to display on the calendar. Events objects
   * can be any shape, as long as the Calendar knows how to retrieve the
   * following details of the event:
   *
   *  - start time
   *  - end time
   *  - title
   *  - whether its an "all day" event or not
   *  - any resource the event may be related to
   *
   * Each of these properties can be customized or generated dynamically by
   * setting the various "accessor" props. Without any configuration the default
   * event should look like:
   *
   * If no events are passed in, the `Calendar` will not render.
   *
   * ```js
   * Event {
   *   title: string,
   *   start: Date,
   *   end: Date,
   *   allDay?: boolean
   *   resource?: any,
   * }
   * ```
   */
  events: _propTypes.default.arrayOf(_propTypes.default.object).isRequired,
  /**
   * @ignore
   * new prop, keeping this in to bring up as a potential future improvement
   *
   * An array of background event objects to display on the calendar. Background
   * Events behave similarly to Events but are not factored into Event overlap logic,
   * allowing them to sit behind any Events that may occur during the same period.
   * Background Events objects can be any shape, as long as the Calendar knows how to
   * retrieve the following details of the event:
   *
   *  - start time
   *  - end time
   *
   * Each of these properties can be customized or generated dynamically by
   * setting the various "accessor" props. Without any configuration the default
   * event should look like:
   *
   * ```js
   * BackgroundEvent {
   *   start: Date,
   *   end: Date,
   * }
   * ```
  */
  backgroundEvents: _propTypes.default.arrayOf(_propTypes.default.object),
  /**
   * Accessor for the event title, used to display event information. Should
   * resolve to a `renderable` value.
   *
   * ```js
   * string | (event: Object) => string
   * ```
   */
  titleAccessor: _propTypes.default.oneOfType([_propTypes.default.func, _propTypes.default.string]),
  /**
   * Accessor for the event tooltip. Should
   * resolve to a `renderable` value. Removes the tooltip if null.
   *
   * ```js
   * string | (event: Object) => string
   * ```
   */
  tooltipAccessor: _propTypes.default.oneOfType([_propTypes.default.func, _propTypes.default.string]),
  /**
   * Determines whether the event should be considered an "all day" event and ignore time.
   * Must resolve to a `boolean` value.
   *
   * ```js
   * string | (event: Object) => boolean
   * ```
   */
  allDayAccessor: _propTypes.default.oneOfType([_propTypes.default.func, _propTypes.default.string]),
  /**
   * The start date/time of the event. Must resolve to a JavaScript `Date` object.
   *
   * ```js
   * string | (event: Object) => Date
   * ```
   */
  startAccessor: _propTypes.default.oneOfType([_propTypes.default.func, _propTypes.default.string]),
  /**
   * The end date/time of the event. Must resolve to a JavaScript `Date` object.
   *
   * ```js
   * string | (event: Object) => Date
   * ```
   */
  endAccessor: _propTypes.default.oneOfType([_propTypes.default.func, _propTypes.default.string]),
  /**
   * Returns the id of the `resource` that the event is a member of. This
   * id should match at least one resource in the `resources` array.
   *
   * ```js
   * string | (event: Object) => Date
   * ```
   */
  resourceAccessor: _propTypes.default.oneOfType([_propTypes.default.func, _propTypes.default.string]),
  /**
   * An array of resource objects that map events to a specific resource.
   * Resource objects, like events, can be any shape or have any properties,
   * but should be uniquly identifiable via the `resourceIdAccessor`, as
   * well as a "title" or name as provided by the `resourceTitleAccessor` prop.
   */
  resources: _propTypes.default.arrayOf(_propTypes.default.object),
  /**
   * Provides a unique identifier for each resource in the `resources` array
   *
   * ```js
   * string | (resource: Object) => any
   * ```
   */
  resourceIdAccessor: _propTypes.default.oneOfType([_propTypes.default.func, _propTypes.default.string]),
  /**
   * Provides a human readable name for the resource object, used in headers.
   *
   * ```js
   * string | (resource: Object) => any
   * ```
   */
  resourceTitleAccessor: _propTypes.default.oneOfType([_propTypes.default.func, _propTypes.default.string]),
  /**
   * Determines the current date/time which is highlighted in the views.
   *
   * The value affects which day is shaded and which time is shown as
   * the current time. It also affects the date used by the Today button in
   * the toolbar.
   *
   * Providing a value here can be useful when you are implementing time zones
   * using the `startAccessor` and `endAccessor` properties.
   *
   */
  getNow: _propTypes.default.func,
  /**
   * Callback fired when the `date` value changes.
   * This is used in custom `Calendar` components.
   * @controllable date
   */
  onNavigate: _propTypes.default.func,
  /**
   * Callback fired when the `view` value changes.
   * This is used in custom `Calendar` components.
   * @controllable view
   */
  onView: _propTypes.default.func,
  /**
   * Callback fired when date header, or the truncated events links are clicked
   *
   */
  onDrillDown: _propTypes.default.func,
  /**
   *
   * ```js
   * (dates: Date[] | { start: Date; end: Date }, view?: 'month'|'week'|'work_week'|'day'|'agenda') => void
   * ```
   *
   * Callback fired when the visible date range changes. Returns an Array of dates
   * or an object with start and end dates for BUILTIN views. Optionally new `view`
   * will be returned when callback called after view change.
   *
   * Custom views may return something different.
   */
  onRangeChange: _propTypes.default.func,
  /**
   * A callback fired when a date selection is made. Only fires when `selectable` is `true`.
   *
   * ```js
   * (
   *   slotInfo: {
   *     start: Date,
   *     end: Date,
   *     slots: Array<Date>,
   *     action: "select" | "click" | "doubleClick",
   *     bounds: ?{ // For "select" action
   *       x: number,
   *       y: number,
   *       top: number,
   *       right: number,
   *       left: number,
   *       bottom: number,
   *     },
   *     box: ?{ // For "click" or "doubleClick" actions
   *       clientX: number,
   *       clientY: number,
   *       x: number,
   *       y: number,
   *     },
   *   }
   * ) => any
   * ```
   */
  onSelectSlot: _propTypes.default.func,
  /**
   * Callback fired when a calendar event is selected.
   *
   * ```js
   * (event: Object, e: SyntheticEvent) => any
   * ```
   *
   * @controllable selected
   */
  onSelectEvent: _propTypes.default.func,
  /**
   * Callback fired when a keypress event occurs on a calendar event.
   *
   * ```js
   * (event: Object, e: SyntheticEvent) => any
   * ```
   *
   * @controllable selected
   */
  onKeyPressEvent: _propTypes.default.func,
  /**
   * Callback fired when a calendar event is clicked twice.
   *
   * ```js
   * (event: Object, e: SyntheticEvent) => void
   * ```
   */
  onDoubleClickEvent: _propTypes.default.func,
  /**
   * Callback fired when dragging a selection in the Time views.
   *
   * Returning `false` from the handler will prevent a selection.
   *
   * ```js
   * (range: { start: Date, end: Date }) => ?boolean
   * ```
   */
  onSelecting: _propTypes.default.func,
  /**
   * The selected event, if any.
   */
  selected: _propTypes.default.object,
  /**
   * An array of built-in view names to allow the calendar to display.
   * accepts either an array of builtin view names,
   *
   * ```jsx
   * views={['month', 'day', 'agenda']}
   * ```
   * or an object hash of the view name and the component (or boolean for builtin).
   *
   * ```jsx
   * views={{
   *   month: true,
   *   week: false,
   *   myweek: WorkWeekViewComponent,
   * }}
   * ```
   *
   * Custom views can be any React component, that implements the following
   * interface:
   *
   * ```js
   * interface View {
   *   static title(date: Date, { formats: DateFormat[], culture: string?, ...props }): string
   *   static navigate(date: Date, action: 'PREV' | 'NEXT' | 'DATE'): Date
   * }
   * ```
   *
   * `@type Calendar.Views ('month'|'week'|'work_week'|'day'|'agenda')`
   * `@View ['month', 'week', 'day', 'agenda']`
   */
  views: _propTypes.default.oneOfType([_propTypes.default.array, _propTypes.default.object]),
  /**
   * The string name of the destination view for drill-down actions, such
   * as clicking a date header, or the truncated events links. If
   * `getDrilldownView` is also specified it will be used instead.
   *
   * Set to `null` to disable drill-down actions.
   *
   * ```js
   * <WeekViewSchedule
   *   drilldownView="agenda"
   * />
   * ```
   */
  drilldownView: _propTypes.default.string,
  /**
   * Functionally equivalent to `drilldownView`, but accepts a function
   * that can return a view name. It's useful for customizing the drill-down
   * actions depending on the target date and triggering view.
   *
   * Return `null` to disable drill-down actions.
   *
   * ```js
   * <WeekViewSchedule
   *   getDrilldownView={(targetDate, currentViewName, configuredViewNames) =>
   *     if (currentViewName === 'month' && (configuredViewNames.indexOf('week') !== -1))
   *       return 'week'
   *
   *     return null;
   *   }}
   * />
   * ```
   */
  getDrilldownView: _propTypes.default.func,
  /**
   * Determines the end date from date prop in the agenda view
   * date prop + length (in number of days) = end date
   */
  length: _propTypes.default.number,
  /**
   * Determines whether the toolbar is displayed
   */
  toolbar: _propTypes.default.bool,
  /**
   * Show truncated events in an overlay when you click the "+_x_ more" link.
   */
  popup: _propTypes.default.bool,
  /**
   * Distance in pixels, from the edges of the viewport, the "show more" overlay should be positioned.
   *
   * ```jsx
   * <WeekViewSchedule popupOffset={30}/>
   * <WeekViewSchedule popupOffset={{x: 30, y: 20}}/>
   * ```
   */
  popupOffset: _propTypes.default.oneOfType([_propTypes.default.number, _propTypes.default.shape({
    x: _propTypes.default.number,
    y: _propTypes.default.number
  })]),
  /**
   * Allows mouse selection of ranges of dates/times.
   *
   * The 'ignoreEvents' option prevents selection code from running when a
   * drag begins over an event. Useful when you want custom event click or drag
   * logic.
   */
  selectable: _propTypes.default.oneOf([true, false, 'ignoreEvents']),
  /**
   * Specifies the number of miliseconds the user must press and hold on the screen for a touch
   * to be considered a "long press." Long presses are used for time slot selection on touch
   * devices.
   *
   */
  longPressThreshold: _propTypes.default.number,
  /**
   * Determines the selectable time increments in week and day views, in minutes.
   */
  step: _propTypes.default.number,
  /**
   * The number of slots per "section" in the time grid views. Adjust with `step`
   * to change the default of 1 hour long groups, with 30 minute slots.
   */
  timeslots: _propTypes.default.number,
  /**
   * Optionally provide a function that returns an object of className or style props
   * to be applied to the the event node.
   *
   * ```js
   * (
   * 	event: Object,
   * 	start: Date,
   * 	end: Date,
   * 	isSelected: boolean
   * ) => { className?: string, style?: Object }
   * ```
   */
  eventPropGetter: _propTypes.default.func,
  /**
   * Optionally provide a function that returns an object of className or style props
   * to be applied to the the time-slot node. Caution! Styles that change layout or
   * position may break the calendar in unexpected ways.
   *
   * ```js
   * (date: Date, resourceId: (number|string)) => { className?: string, style?: Object }
   * ```
   */
  slotPropGetter: _propTypes.default.func,
  /**
   * Optionally provide a function that returns an object of className or style props
   * to be applied to the the day background. Caution! Styles that change layout or
   * position may break the calendar in unexpected ways.
   *
   * ```js
   * (date: Date) => { className?: string, style?: Object }
   * ```
   */
  dayPropGetter: _propTypes.default.func,
  /**
   * Support to show multi-day events with specific start and end times in the
   * main time grid (rather than in the all day header).
   *
   * **Note: This may cause calendars with several events to look very busy in
   * the week and day views.**
   */
  showMultiDayTimes: _propTypes.default.bool,
  /**
   * Constrains the minimum _time_ of the Day and Week views.
   * This prop expects a `Date` to be sent in.
   */
  min: _propTypes.default.instanceOf(Date),
  /**
   * Constrains the maximum _time_ of the Day and Week views.
   * This prop expects a `Date` to be sent in.
   */
  max: _propTypes.default.instanceOf(Date),
  /**
   * Determines how far down the scroll pane is initially scrolled down.
   */
  scrollToTime: _propTypes.default.instanceOf(Date),
  /**
   * @ignore
   */
  edsContext: _propTypes.default.object,
  /**
   * Localizer specific formats, tell the Calendar how to format and display dates.
   *
   * `format` types are dependent on the configured localizer; date-fns, Moment and Globalize
   * accept strings of tokens according to their own specification, such as: `'DD mm yyyy'`.
   *
   * ```jsx
   * let formats = {
   *   dateFormat: 'dd',
   *
   *   dayFormat: (date, culture, localizer) =>
   *     localizer.format(date, 'DDD', culture),
   *
   *   dayRangeHeaderFormat: ({ start, end }, culture, localizer) =>
   *     localizer.format(start, { date: 'short' }, culture) + ' — ' +
   *     localizer.format(end, { date: 'short' }, culture)
   * }
   *
   * <Calendar formats={formats} />
   * ```
   *
   * All localizers accept a function of
   * the form `(date: Date, culture: ?string, localizer: Localizer) -> string`
   */
  formats: _propTypes.default.shape({
    /**
     * Format for the day of the month heading in the Month view.
     * e.g. "01", "02", "03", etc
     */
    dateFormat: dateFormat,
    /**
     * A day of the week format for Week and Day headings,
     * e.g. "Wed 01/04"
     *
     */
    dayFormat: dateFormat,
    /**
     * Week day name format for the Month week day headings,
     * e.g: "Sun", "Mon", "Tue", etc
     *
     */
    weekdayFormat: dateFormat,
    /**
     * The timestamp cell formats in Week and Time views, e.g. "4:00 AM"
     */
    timeGutterFormat: dateFormat,
    /**
     * Toolbar header format for the Month view, e.g "2015 April"
     *
     */
    monthHeaderFormat: dateFormat,
    /**
     * Toolbar header format for the Week views, e.g. "Mar 29 - Apr 04"
     */
    dayRangeHeaderFormat: dateRangeFormat,
    /**
     * Toolbar header format for the Day view, e.g. "Wednesday Apr 01"
     */
    dayHeaderFormat: dateFormat,
    /**
     * Toolbar header format for the Agenda view, e.g. "4/1/2015 — 5/1/2015"
     */
    agendaHeaderFormat: dateRangeFormat,
    /**
     * A time range format for selecting time slots, e.g "8:00am — 2:00pm"
     */
    selectRangeFormat: dateRangeFormat,
    agendaDateFormat: dateFormat,
    agendaTimeFormat: dateFormat,
    agendaTimeRangeFormat: dateRangeFormat,
    /**
     * Time range displayed on events.
     */
    eventTimeRangeFormat: dateRangeFormat,
    /**
     * An optional event time range for events that continue onto another day
     */
    eventTimeRangeStartFormat: dateFormat,
    /**
     * An optional event time range for events that continue from another day
     */
    eventTimeRangeEndFormat: dateFormat
  }),
  /**
   * Customize how different sections of the calendar render by providing custom Components.
   * In particular the `Event` component can be specified for the entire calendar, or you can
   * provide an individual component for each view type.
   *
   * ```jsx
   * let components = {
   *   event: MyEvent, // used by each view (Month, Day, Week)
   *   eventWrapper: MyEventWrapper,
   *   eventContainerWrapper: MyEventContainerWrapper,
   *   dayWrapper: MyDayWrapper,
   *   dateCellWrapper: MyDateCellWrapper,
   *   timeSlotWrapper: MyTimeSlotWrapper,
   *   timeGutterHeader: MyTimeGutterWrapper,
   *   toolbar: MyToolbar,
   *   agenda: {
   *   	 event: MyAgendaEvent // with the agenda view use a different component to render events
   *     time: MyAgendaTime,
   *     date: MyAgendaDate,
   *   },
   *   day: {
   *     header: MyDayHeader,
   *     event: MyDayEvent,
   *   },
   *   week: {
   *     header: MyWeekHeader,
   *     event: MyWeekEvent,
   *   },
   *   month: {
   *     header: MyMonthHeader,
   *     dateHeader: MyMonthDateHeader,
   *     event: MyMonthEvent,
   *   }
   * }
   * <Calendar components={components} />
   * ```
   */
  components: _propTypes.default.shape({
    event: _propTypes.default.elementType,
    eventWrapper: _propTypes.default.elementType,
    eventContainerWrapper: _propTypes.default.elementType,
    dayWrapper: _propTypes.default.elementType,
    dateCellWrapper: _propTypes.default.elementType,
    timeSlotWrapper: _propTypes.default.elementType,
    timeGutterHeader: _propTypes.default.elementType,
    toolbar: _propTypes.default.elementType,
    agenda: _propTypes.default.shape({
      date: _propTypes.default.elementType,
      time: _propTypes.default.elementType,
      event: _propTypes.default.elementType
    }),
    day: _propTypes.default.shape({
      header: _propTypes.default.elementType,
      event: _propTypes.default.elementType
    }),
    week: _propTypes.default.shape({
      header: _propTypes.default.elementType,
      event: _propTypes.default.elementType
    }),
    month: _propTypes.default.shape({
      header: _propTypes.default.elementType,
      dateHeader: _propTypes.default.elementType,
      event: _propTypes.default.elementType
    })
  }),
  /**
   * Use this to add legends to calendar. You can use CalendarLegend component render legends.
   */
  legend: _propTypes.default.node,
  /**
   * If true, displays the dropdown with view names in the Toolbar
   */
  showViewNames: _propTypes.default.bool,
  /**
   * If true, displays the current day of the week when view is set to `day`
   */
  showOnlyDay: _propTypes.default.bool,
  /**
   * @ignore
   * A string representation of the current viewport
   */
  width: _propTypes.default.string.isRequired,
  /**
   * Content to be rendered on Popper for each event click
   *
   * You can pass a render function or a node.
   *
   * * If you pass this prop as a node, then you will have to set the selected event through `onSelectEvent()` and `onKeyPressEvent` callback.
   * * If you pass this prop as a render function, then the selected `event` will be passed as an argument to the render function.
   */
  popperContent: _propTypes.default.oneOfType([_propTypes.default.node, _propTypes.default.func])
};
Calendar.defaultProps = {
  elementProps: {},
  popup: false,
  toolbar: true,
  // view: views.WEEK, // NOTE: breaks component if uncommented
  // trying to set view with a defaultProp breaks it. Looks like it conflicts with defaultView prop
  defaultView: 'month',
  views: [_reactBigCalendar.Views.WEEK, _reactBigCalendar.Views.WORK_WEEK],
  step: 30,
  length: 30,
  drilldownView: _reactBigCalendar.Views.DAY,
  titleAccessor: 'title',
  tooltipAccessor: 'title',
  allDayAccessor: 'allDay',
  startAccessor: 'start',
  endAccessor: 'end',
  resourceAccessor: 'resourceId',
  resourceIdAccessor: 'id',
  resourceTitleAccessor: 'title',
  longPressThreshold: 250,
  getNow: function getNow() {
    return new Date();
  },
  showViewNames: true,
  showOnlyDay: false,
  showMultiDayTimes: true
};
Calendar.displayName = 'Calendar';
var _default = exports.default = (0, _withWidth.default)()((0, _EDSContext.withEDSContext)((0, _styles.default)(_ReactBigCalendarStyles.default)(Calendar)));